"use client";

import { c as _c } from "react/compiler-runtime";
import type { ImageProps } from "next/image";
import Image from "next/image";
import type React from "react";
import { useEffect, useState } from "react";
import { SvgImage } from "@/components/svg-image";
import { isSvgDataUrl } from "@/lib/svg-icon";
interface OptimizedImageProps extends Omit<ImageProps, "onError" | "onLoad" | "src"> {
  src: string | null;
  fallbackSrc?: string;
  alt: string; // Make alt explicitly required for accessibility
  containerClassName?: string; // Optional class for the wrapper div
}
const MINIMAL_FALLBACK_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50' y='55' font-family='sans-serif' font-size='10' text-anchor='middle' fill='%23bbb'%3EError%3C/text%3E%3C/svg%3E";

/** Check if an image source should skip Next.js optimization */
function shouldSkipOptimization(source: string): boolean {
  return typeof source === "string" && (source.startsWith("data:") || source.startsWith("/api/image/fetch/") || source.startsWith("https://api.1sat.app/"));
}

/** Build wrapper classes for the image container, ensuring fill mode has dimensions */
function buildWrapperClasses(containerClassName: string | undefined, isFill: boolean | undefined): string {
  const classes = [containerClassName || "", "relative"];
  if (isFill) {
    const hasWidth = classes.some(cls => cls.includes("w-") || cls.includes("width"));
    if (!hasWidth) {
      classes.push("w-full");
    }
    const hasHeight = classes.some(cls => cls.includes("aspect-") || cls.includes("h-") || cls.includes("height"));
    if (!hasHeight) {
      classes.push("aspect-video");
    }
  }
  return classes.join(" ").trim();
}
function buildImageClassName({
  className,
  isVisible,
  fillSvg
}: {
  className?: string;
  isVisible: boolean;
  fillSvg: boolean;
}): string {
  return ["transition-opacity duration-300 ease-in-out", className, isVisible ? "opacity-100" : "opacity-0", fillSvg ? "absolute inset-0 h-full w-full" : ""].filter(Boolean).join(" ");
}
function RenderedSource(t0) {
  const $ = _c(18);
  const {
    alt,
    className,
    source,
    onError,
    onLoad,
    imageProps
  } = t0;
  if (isSvgDataUrl(source)) {
    const t1 = typeof imageProps.height === "number" ? imageProps.height : undefined;
    const t2 = typeof imageProps.width === "number" ? imageProps.width : undefined;
    let t3;
    if ($[0] !== alt || $[1] !== className || $[2] !== onError || $[3] !== onLoad || $[4] !== source || $[5] !== t1 || $[6] !== t2) {
      t3 = <SvgImage alt={alt} className={className} height={t1} onError={onError} onLoad={onLoad} src={source} width={t2} />;
      $[0] = alt;
      $[1] = className;
      $[2] = onError;
      $[3] = onLoad;
      $[4] = source;
      $[5] = t1;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t1;
  if ($[8] !== source) {
    t1 = shouldSkipOptimization(source) ? {
      unoptimized: true
    } : {};
    $[8] = source;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const unoptimizedProp = t1;
  let t2;
  if ($[10] !== alt || $[11] !== className || $[12] !== imageProps || $[13] !== onError || $[14] !== onLoad || $[15] !== source || $[16] !== unoptimizedProp) {
    t2 = <Image alt={alt} className={className} onError={onError} onLoad={onLoad} src={source} {...unoptimizedProp} {...imageProps} />;
    $[10] = alt;
    $[11] = className;
    $[12] = imageProps;
    $[13] = onError;
    $[14] = onLoad;
    $[15] = source;
    $[16] = unoptimizedProp;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
}
const OptimizedImage: React.FC<OptimizedImageProps> = t0 => {
  const $ = _c(38);
  const {
    src,
    fallbackSrc,
    alt,
    containerClassName,
    className,
    ...props
  } = t0;
  const [currentSrc, setCurrentSrc] = useState(src || fallbackSrc || MINIMAL_FALLBACK_SVG);
  const [isLoading, setIsLoading] = useState(!!src);
  const [hasAttemptedFallback, setHasAttemptedFallback] = useState(!src);
  const [hasFinalError, setHasFinalError] = useState(!(src || fallbackSrc));
  let t1;
  if ($[0] !== fallbackSrc || $[1] !== setHasFinalError || $[2] !== setIsLoading || $[3] !== src) {
    t1 = () => {
      if (src) {
        setIsLoading(true);
        setCurrentSrc(src);
        setHasAttemptedFallback(false);
        setHasFinalError(false);
        return;
      }
      setCurrentSrc(fallbackSrc || MINIMAL_FALLBACK_SVG);
      setIsLoading(false);
      setHasAttemptedFallback(true);
      setHasFinalError(!fallbackSrc);
    };
    $[0] = fallbackSrc;
    $[1] = setHasFinalError;
    $[2] = setIsLoading;
    $[3] = src;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== fallbackSrc || $[6] !== src) {
    t2 = [src, fallbackSrc];
    $[5] = fallbackSrc;
    $[6] = src;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== fallbackSrc || $[9] !== hasAttemptedFallback || $[10] !== setHasFinalError || $[11] !== setIsLoading) {
    t3 = () => {
      if (!hasAttemptedFallback && fallbackSrc) {
        setCurrentSrc(fallbackSrc);
        setHasAttemptedFallback(true);
        setIsLoading(true);
        setHasFinalError(false);
        return;
      }
      setHasFinalError(true);
      setIsLoading(false);
    };
    $[8] = fallbackSrc;
    $[9] = hasAttemptedFallback;
    $[10] = setHasFinalError;
    $[11] = setIsLoading;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleError = t3;
  const imageSourceToRender = hasFinalError ? MINIMAL_FALLBACK_SVG : currentSrc;
  const wrapperClassName = buildWrapperClasses(containerClassName, props.fill);
  const t4 = !isLoading || hasFinalError;
  let t5;
  if ($[13] !== className || $[14] !== imageSourceToRender || $[15] !== props || $[16] !== t4) {
    t5 = buildImageClassName({
      className,
      isVisible: t4,
      fillSvg: isSvgDataUrl(imageSourceToRender) && Boolean(props.fill)
    });
    $[13] = className;
    $[14] = imageSourceToRender;
    $[15] = props;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const imageClassName = t5;
  let t6;
  if ($[18] !== alt || $[19] !== className || $[20] !== hasFinalError || $[21] !== isLoading) {
    t6 = isLoading && !hasFinalError && <output aria-label={alt || "Loading image"} className={`absolute inset-0 animate-pulse bg-neutral-content/10 ${className || ""}`.trim()} />;
    $[18] = alt;
    $[19] = className;
    $[20] = hasFinalError;
    $[21] = isLoading;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const t7 = currentSrc as React.Key;
  let t8;
  if ($[23] !== setHasFinalError || $[24] !== setIsLoading) {
    t8 = () => {
      setIsLoading(false);
      setHasFinalError(false);
    };
    $[23] = setHasFinalError;
    $[24] = setIsLoading;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = imageSourceToRender || MINIMAL_FALLBACK_SVG;
  let t10;
  if ($[26] !== alt || $[27] !== handleError || $[28] !== imageClassName || $[29] !== props || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t10 = <RenderedSource alt={alt} className={imageClassName} imageProps={props} key={t7} onError={handleError} onLoad={t8} source={t9} />;
    $[26] = alt;
    $[27] = handleError;
    $[28] = imageClassName;
    $[29] = props;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10 || $[35] !== t6 || $[36] !== wrapperClassName) {
    t11 = <div className={wrapperClassName}>{t6}{t10}</div>;
    $[34] = t10;
    $[35] = t6;
    $[36] = wrapperClassName;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
};
export default OptimizedImage;